import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useErrorToast } from '../components/common';
import { toSafeImageUrl } from '../components/common/safeUrl';
import CertificateFontDialog from '../components/certificate/CertificateFontDialog';
import CertificateTemplateEditor from '../components/certificate/CertificateTemplateEditor';
import {
  certificateTemplateService,
  type CertificateTemplateField,
  type CertificateTemplateListItem,
} from '../services/certificateTemplateService';
import { certificateFontService, type CertificateFont } from '../services/certificateFontService';
import { eventService, type EventSummary } from '../services/eventService';

function readImageDimensions(
  src: string,
  allowDataImage = false
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const safeSrc = toSafeImageUrl(src, { allowDataImage });
    if (!safeSrc) {
      reject(new Error('안전하지 않은 이미지 URL입니다.'));
      return;
    }

    const image = new window.Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('이미지를 불러오지 못했습니다.'));
    image.src = safeSrc;
  });
}

function readImageFile(file: File): Promise<{ dataUrl: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('이미지를 읽지 못했습니다.'));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      readImageDimensions(dataUrl, true)
        .then(({ width, height }) => resolve({ dataUrl, width, height }))
        .catch(reject);
    };
    reader.readAsDataURL(file);
  });
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

type FormState = {
  id: number | null;
  eventId: number | null;
  eventTitle: string;
  /** 미리보기용 src: 새로 고른 파일이면 data URL, 기존 템플릿이면 서버 imageUrl */
  imageSrc: string;
  /** 새로 고른 이미지 파일. null이면 이미지를 바꾸지 않는다(수정 시 기존 이미지 유지). */
  imageFile: File | null;
  imageWidth: number;
  imageHeight: number;
  fields: CertificateTemplateField[];
  createdAt: string | null;
};

const emptyForm: FormState = {
  id: null,
  eventId: null,
  eventTitle: '',
  imageSrc: '',
  imageFile: null,
  imageWidth: 0,
  imageHeight: 0,
  fields: [],
  createdAt: null,
};

function CertificateTemplatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const editId = searchParams.get('id');
  const isFormMode = mode === 'create' || mode === 'edit';

  const [templates, setTemplates] = useState<CertificateTemplateListItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { showError, errorToast } = useErrorToast();
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [fonts, setFonts] = useState<CertificateFont[]>([]);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isFormMode) {
      return;
    }

    certificateTemplateService
      .list()
      .then(setTemplates)
      .catch((error) => showError(error, '템플릿 목록을 불러오지 못했습니다.'));
  }, [isFormMode, showError]);

  useEffect(() => {
    // 템플릿 편집 화면에서만 필드별 폰트 선택지가 필요하다
    if (!isFormMode) {
      return;
    }

    let ignore = false;
    certificateFontService
      .list()
      .then((data) => {
        if (!ignore) {
          setFonts(data);
        }
      })
      .catch((error) => {
        if (!ignore) {
          showError(error, '폰트 목록을 불러오지 못했습니다.');
        }
      });

    return () => {
      ignore = true;
    };
  }, [isFormMode, showError]);

  useEffect(() => {
    // 수정 모드는 대회를 바꿀 수 없어 목록이 필요 없으므로, 새 템플릿 등록 때만 불러온다
    if (mode !== 'create') {
      return;
    }

    let ignore = false;
    setEventsLoading(true);
    eventService
      .list()
      .then((data) => {
        if (!ignore) {
          setEvents(data);
        }
      })
      .catch((error) => {
        if (!ignore) {
          showError(error, '대회 목록을 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setEventsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [mode, showError]);

  useEffect(() => {
    if (mode === 'create') {
      setForm(emptyForm);
      return;
    }

    if (mode === 'edit' && editId) {
      let ignore = false;

      certificateTemplateService
        .detail(Number(editId))
        .then((template) => {
          const safeImageUrl = toSafeImageUrl(template.imageUrl);

          return readImageDimensions(safeImageUrl).then(({ width, height }) => {
            if (ignore) {
              return;
            }
            setForm({
              id: template.id,
              eventId: template.eventId,
              eventTitle: template.eventTitle,
              imageSrc: safeImageUrl,
              imageFile: null,
              imageWidth: width,
              imageHeight: height,
              // 폰트 지정 기능 이전에 저장된 템플릿은 fontId가 없을 수 있어 null(기본 폰트)로 맞춘다
              fields: template.fields.map((field) => ({ ...field, fontId: field.fontId ?? null })),
              createdAt: template.createdAt,
            });
          });
        })
        .catch((error) => {
          if (!ignore) {
            showError(error, '수정할 템플릿을 불러오지 못했습니다.');
          }
        });

      return () => {
        ignore = true;
      };
    }
  }, [editId, mode, showError]);

  // 대회당 템플릿은 하나뿐이라, 이미 다른 템플릿이 쓰고 있는 대회는 선택지에서 뺀다 (수정 중인 대회 자신은 유지)
  const usedEventIds = new Set(
    templates.filter((template) => template.id !== form.id).map((template) => template.eventId)
  );
  const selectableEvents = events.filter((event) => !usedEventIds.has(event.id));
  const selectedEvent = events.find((event) => event.id === form.eventId) ?? null;

  const handleImageSelect = async (file: File | null) => {
    if (!file) {
      return;
    }

    try {
      const { dataUrl, width, height } = await readImageFile(file);
      setForm((current) => ({
        ...current,
        imageSrc: dataUrl,
        imageFile: file,
        imageWidth: width,
        imageHeight: height,
      }));
    } catch (error) {
      showError(error, '이미지를 불러오지 못했습니다.');
    }
  };

  const handleSave = async () => {
    if (!form.eventId) {
      showError(new Error('완주증을 만들 대회를 선택해 주세요.'));
      return;
    }

    if (!form.id && !form.imageFile) {
      showError(new Error('템플릿 이미지를 업로드해 주세요.'));
      return;
    }

    setSaving(true);
    try {
      if (form.id) {
        await certificateTemplateService.update(form.id, { fields: form.fields }, form.imageFile);
      } else {
        await certificateTemplateService.create(
          { eventId: form.eventId, fields: form.fields },
          form.imageFile as File
        );
      }
      navigate('/homepage/certificate-template');
    } catch (error) {
      showError(error, '템플릿을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId === null) {
      return;
    }

    const targetId = deleteTargetId;
    try {
      await certificateTemplateService.delete(targetId);
      setTemplates((current) => current.filter((template) => template.id !== targetId));
    } catch (error) {
      showError(error, '템플릿을 삭제하지 못했습니다.');
    } finally {
      setDeleteTargetId(null);
    }
  };

  if (isFormMode) {
    const title = mode === 'create' ? '완주증 템플릿 등록' : '완주증 템플릿 수정';

    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <h1 style={{ margin: 0 }}>{title}</h1>
            <Button onClick={() => navigate('/homepage/certificate-template')} variant="outlined">
              목록으로
            </Button>
          </Box>

          <Paper sx={{ p: 3 }} variant="outlined">
            <Box sx={{ display: 'grid', gap: 2, mb: 3, maxWidth: 480 }}>
              {mode === 'edit' ? (
                <Typography sx={{ color: '#0f172a', fontSize: 20, fontWeight: 800 }}>
                  {form.eventTitle}
                </Typography>
              ) : (
                <Autocomplete
                  getOptionLabel={(event) => `${event.title} · ${event.eventDate} · ${event.city}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  loading={eventsLoading}
                  onChange={(_, event) =>
                    setForm((current) => ({
                      ...current,
                      eventId: event?.id ?? null,
                      eventTitle: event?.title ?? '',
                    }))
                  }
                  options={selectableEvents}
                  renderInput={(params) => (
                    <TextField {...params} label="대회 선택" placeholder="대회명으로 검색" required />
                  )}
                  value={selectedEvent}
                />
              )}
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                <Button onClick={() => fileInputRef.current?.click()} variant="outlined">
                  {form.imageSrc ? '템플릿 이미지 변경' : '템플릿 이미지 업로드'}
                </Button>
                {form.imageWidth > 0 && (
                  <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                    {form.imageWidth} x {form.imageHeight}px
                  </Typography>
                )}
                <input
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    void handleImageSelect(event.target.files?.[0] ?? null);
                    event.target.value = '';
                  }}
                  ref={fileInputRef}
                  type="file"
                />
              </Box>
            </Box>

            <CertificateTemplateEditor
              fields={form.fields}
              fonts={fonts}
              imageSrc={form.imageSrc}
              imageHeight={form.imageHeight}
              imageWidth={form.imageWidth}
              onFieldRemoved={(message) => showError(new Error(message))}
              onFieldsChange={(fields) => setForm((current) => ({ ...current, fields }))}
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => navigate('/homepage/certificate-template')} variant="outlined">
                취소
              </Button>
              <Button disabled={saving} onClick={handleSave} variant="contained">
                저장
              </Button>
            </Box>
          </Paper>
        </div>
        {errorToast}
      </section>
    );
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Homepage</p>
          <h1>완주증 템플릿 등록</h1>
          <p className="page-description">
            완주증 이미지를 업로드하고 참가자 이름, 기록 등 데이터가 들어갈 위치를 지정합니다.
          </p>
        </div>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 3 }}>
          <Button onClick={() => setFontDialogOpen(true)} variant="outlined">
            폰트 관리
          </Button>
          <Button onClick={() => navigate('/homepage/certificate-template?mode=create')} variant="contained">
            새 템플릿 등록
          </Button>
        </Box>

        {templates.length === 0 ? (
          <Paper sx={{ color: '#64748b', p: 4, textAlign: 'center' }} variant="outlined">
            등록된 템플릿이 없습니다.
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {templates.map((template) => {
              const safeImageUrl = toSafeImageUrl(template.imageUrl);

              return (
              <Paper key={template.id} sx={{ overflow: 'hidden' }} variant="outlined">
                {safeImageUrl ? (
                  <Box
                    component="img"
                    src={safeImageUrl}
                    sx={{ backgroundColor: '#f1f5f9', display: 'block', height: 150, objectFit: 'contain', width: '100%' }}
                  />
                ) : (
                  <Box sx={{ backgroundColor: '#f1f5f9', height: 150, width: '100%' }} />
                )}
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{template.eventTitle}</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: 12, mb: 1.5, mt: 0.5 }}>
                    {formatDate(template.updatedAt)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      onClick={() => navigate(`/homepage/certificate-template?mode=edit&id=${template.id}`)}
                      size="small"
                      variant="outlined"
                    >
                      수정
                    </Button>
                    <Button
                      color="error"
                      fullWidth
                      onClick={() => setDeleteTargetId(template.id)}
                      size="small"
                      variant="outlined"
                    >
                      삭제
                    </Button>
                  </Box>
                </Box>
              </Paper>
              );
            })}
          </Box>
        )}
      </div>

      <CertificateFontDialog onClose={() => setFontDialogOpen(false)} open={fontDialogOpen} />

      <Dialog fullWidth maxWidth="xs" onClose={() => setDeleteTargetId(null)} open={deleteTargetId !== null}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>템플릿 삭제</DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#334155', fontSize: 14 }}>이 템플릿을 삭제할까요? 삭제 후 되돌릴 수 없습니다.</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)}>취소</Button>
          <Button color="error" disableElevation onClick={handleDeleteConfirm} variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
      {errorToast}
    </section>
  );
}

export default CertificateTemplatePage;
